//-- Copyright (c) 2021 TMT Insights Group, LLC
import { useEffect } from 'react';
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import {
  selectAppLoading,
  selectCanReadProfiles,
  selectCanViewTitleCustomer,
  selectCanReadTitleCustomer,
  selectUserPreferences,
  selectCanViewAvails
} from './redux/selectors/authSelectors';
import { PolarisRoute, authRoutes, getPolarisRouteFromNavLinks, privateRoutes, publicRoutes } from './view/polarisRoutes';
import AuthRoute from './view/shared/routes/AuthRoute';
import PrivateRoute from './view/shared/routes/PrivateRoute';
import PublicPage from './view/shared/routes/PublicPage';
import Theme from './components/Theme';
import Snackbar from './components/SnackBar';
import { setUserColumns } from './_utils/columns';
import { setDataTables } from './redux/slices/dataTableSlice';
import { initDataTables, setConfigParseFns, setGraphList, setOrderPlacementConfig, setNotificationsVersion } from './redux/selectors/configSelectors';
import { getConstants, getCustomers, getFocusConstants, getLanguages } from './redux/slices/constantsSlice';
import { injectDoLogout, injectStore as injectStoreToAxios } from './_utils/axios';
import { injectStore as injectStoreToOrderUtils } from './_utils/orderUtils';
import { doLogout, getPreferences } from './redux/slices/authSlice';
import { ConfigContext } from '@/contextAPI/configContext';
import store from './redux/store';
import NoRoute from './components/NoRoute/NoRoute';
import { setDashboardConfig } from './redux/slices/dashboardSlice';
import useAppVersion from './components/_hooks/useAppVersion';
import { ClientConfig, DataTableConfig } from './__configs/config_types';
import { WebSocketProvider } from './contextAPI/websocketsContext';
import { getAllProfiles } from './redux/slices/profileSlice';
import { useAppDispatch, useAppSelector } from './redux/hooks';
import ReleaseNotes from './components/_common/ReleaseNotes/ReleaseNotes';
import { enableMapSet } from 'immer';
import { I18nProvider } from './components/_common/I18nProvider';

enableMapSet();

type AppProps = {
  config?: ClientConfig;
  configOptions?: {
    key: string;
    label: string;
  }[];
};

function App({ config = {} as ClientConfig, configOptions = [] }: AppProps) {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(selectAppLoading);
  const userColors = config.userColors;
  const userPreferences = useAppSelector(selectUserPreferences);
  const canReadProfiles = useAppSelector(selectCanReadProfiles);
  const canViewTitleCustomer = useAppSelector(selectCanViewTitleCustomer);
  const canReadTitleCustomer = useAppSelector(selectCanReadTitleCustomer);
  const customClientRoutes = config.navLinks && Object.keys(config.navLinks).length > 0 ? getPolarisRouteFromNavLinks(config.navLinks) : [];
  const canViewAvails = useAppSelector(selectCanViewAvails);

  useAppVersion();
  injectStoreAndActions();
  setUserColumns(config);

  useEffect(() => {
    dispatch(getConstants(config));
    dispatch(getLanguages());
    dispatch(getPreferences(userColors));
    dispatch(setDataTables(initDataTables(config.dataConfig) as DataTableConfig & void));
    dispatch(setDashboardConfig(config.dashboardConfig));
    setConfigParseFns(config);
    setGraphList(config);
    setOrderPlacementConfig(config);
    setNotificationsVersion(config);

    // this is temporary until ingest v1 is deprecated
    const ingestVersion = config.ingestManagementConfig?.version;
    sessionStorage.setItem('ingestVersion', ingestVersion || 'v1');
  }, [config]);

  useEffect(() => {
    if (canReadProfiles) {
      dispatch(getAllProfiles());
    }
  }, [canReadProfiles]);

  useEffect(() => {
    if (canViewAvails) {
      dispatch(getFocusConstants());
    }
  }, [canViewAvails]);

  useEffect(() => {
    if (canViewTitleCustomer || canReadTitleCustomer) {
      dispatch(getCustomers());
    }
  }, [canViewTitleCustomer, canReadTitleCustomer]);

  useEffect(() => {
    if (userPreferences.date_format) {
      localStorage.setItem('date_format', userPreferences.date_format);
    }
    if (userPreferences.time_format) {
      localStorage.setItem('time_format', userPreferences.time_format);
    }
  }, [userPreferences.date_format, userPreferences.time_format]);

  if (loading) return <div />;

  return (
    <WebSocketProvider>
      <Theme config={config}>
        <ConfigContext.Provider value={config}>
          <CssBaseline />
          <Snackbar>
            <ReleaseNotes>
              <I18nProvider locale="en" namespace={config.localization?.namespace || 'default'}>
                <Router>
                  <Switch>
                    {[...privateRoutes, ...customClientRoutes].map((route: PolarisRoute) => (
                      <PrivateRoute
                        key={route.path}
                        path={route.path}
                        exact={route.exact}
                        component={route.component}
                        role={route.role}
                        ignoreScrollBehavior={route.ignoreScrollBehavior || false}
                        calcTitle={route.calcTitle}
                        configOptions={configOptions}
                      />
                    ))}
                    {authRoutes.map((route) => (
                      <AuthRoute
                        key={route.path}
                        path={route.path}
                        exact={route.exact}
                        component={route.component}
                        ignoreScrollBehavior={route.ignoreScrollBehavior || false}
                      />
                    ))}
                    <Route path="/no-access" exact component={NoRoute} />
                    {publicRoutes.map((route: PolarisRoute) => (
                      <PublicPage
                        key={route.path}
                        path={route.path}
                        exact={route.exact}
                        component={route.component}
                        role={route.role}
                        ignoreScrollBehavior={route.ignoreScrollBehavior || false}
                        configOptions={configOptions}
                      />
                    ))}
                  </Switch>
                </Router>
              </I18nProvider>
            </ReleaseNotes>
          </Snackbar>
        </ConfigContext.Provider>
      </Theme>
    </WebSocketProvider>
  );
}

export default App;

const injectStoreAndActions = () => {
  injectStoreToAxios(store);
  injectStoreToOrderUtils(store);
  injectDoLogout(doLogout);
};
